import { FC } from 'react';
import { OrderProperty } from 'types/order';
import {
  Box,
  Divider,
  Paper,
  Typography,
} from '@mui/material';

import { FormProration } from './components/form-proration';
import { FormTaxInfo } from './components/form-tax-info';
import { Header } from './components/header';
import { Options } from './components/options';
import { Tabs } from './components/tabs';

import { useTaxesAndProrations } from './hooks';
import { useStyles } from './styles';
import SelectField from 'components/select-field';
import { FormProvider } from 'react-hook-form';
import { CreateProrationModal } from './components/create-proration-modal';
import { ProratedAmount } from './components/prorated-amount-form';
import { ProrateUsingForm } from './components/prorate-using-form';
import { useTranslation } from 'react-i18next';
import { DebitedToForm } from './components/debited-to-form';
import { FlagsForm } from './components/flags-form';
import { DeleteConfirmationModal } from 'components/delete-confirmation';
import { Link, useParams } from 'react-router-dom';

type TaxesAndProrationsProps = {
  properties: OrderProperty[]
}

export const TaxesAndProrations: FC<TaxesAndProrationsProps> = ({ properties }) => {
  const { id: orderId } = useParams<{ id: string }>();
  const {
    selectedProperty,
    selectProperty,
    prorationsForm,
    selectedTabIndex,
    setSelectedTabIndex,
    modalData,
    setModalData,
    onSave,
    onDelete,
    onConfirmDelete,
    onCancelDelete,
    isDeleteLoading,
    isDeleteDialogOpen,
    onCreated,
    isLoading
  } = useTaxesAndProrations({ properties });
  const classes = useStyles();

  const onClose = () => setModalData({ modal: '' });
  const { t } = useTranslation();

  return (
    <Box className={classes.rootContainer}>
      <Paper>
        <FormProvider {...prorationsForm}>
          <>
            <Header
              t={t}
              isValid
              showAddProration={Boolean(selectedProperty)}
              disableAddProration={isLoading || selectedProperty === undefined}
              openCreateProrationModal={() => setModalData({ modal: "createProrationModal" })}
            />

            {properties.length > 1 && (
              <Box className={classes.selectPropertyContainer}>
                <SelectField
                  label={t('orders:propertiesLabel')}
                  emptyValue={false}
                  value={selectedProperty?._id ?? ''}
                  data={properties.map((property) => ({
                    _id: property._id,
                    address: property.address.address,
                  }))}
                  dataKey="_id"
                  dataValue="_id"
                  dataText="address"
                  handleChange={(e) => {
                    setSelectedTabIndex(0);
                    selectProperty(e.target.value);
                  }}
                  disabled={isLoading}
                />
              </Box>
            )}
            {properties.length === 1 && selectedProperty && (
              <Box className={classes.selectPropertyContainer}>
                <Typography variant="body2">{t('orders:propertyLabel')}</Typography>
                <Typography>{selectedProperty.address.address}</Typography>
              </Box>
            )}

            <Box className={classes.content}>
              {(prorationsForm.getValues("forms").length > 0 && selectedProperty) ? (
                <>
                  <Box className={classes.navigation}>
                    <Tabs
                      selectedTabIndex={selectedTabIndex}
                      onDelete={onDelete}
                      onSelectTab={(index) => setSelectedTabIndex(index)}
                      disabledDelete={isLoading}
                    />
                    <Options
                      onSave={onSave}
                      isLoading={isLoading}
                    />
                  </Box>
                  <Box className={classes.container}>
                    <Box className={classes.formColumn}>
                      {selectedProperty && (
                        <FormTaxInfo
                          t={t}
                          selectedProperty={selectedProperty}
                          selectedTabIndex={selectedTabIndex}
                          isLoading={isLoading}
                        />
                      )}
                      <FormProration
                        t={t}
                        selectedTabIndex={selectedTabIndex}
                        isLoading={isLoading}
                      />
                      <ProratedAmount
                        selectedTabIndex={selectedTabIndex}
                        isLoading={isLoading}
                      />
                    </Box>

                    <Divider orientation="vertical" className={classes.dividerVertical} />

                    <Box className={classes.formColumn}>
                      <ProrateUsingForm
                        selectedTabIndex={selectedTabIndex}
                        isLoading={isLoading}
                      />

                      <DebitedToForm
                        selectedTabIndex={selectedTabIndex}
                        isLoading={isLoading}
                      />

                      <FlagsForm
                        selectedTabIndex={selectedTabIndex}
                        isLoading={isLoading}
                      />
                    </Box>
                  </Box>
                </>
              ) : (
                <Typography>
                  {t('orders:noPropertyMessage')}
                  {' '}
                  <Link to={`/orders/details/${orderId}/properties`}>
                    {t('orders:goToProperties')}
                  </Link>
                </Typography>
              )}
            </Box>
          </>
        </FormProvider>
      </Paper>
      {modalData.modal === "createProrationModal" && (
        <CreateProrationModal
          selectedProperty={selectedProperty!}
          onClose={onClose}
          onCreated={onCreated}
        />
      )}
      <DeleteConfirmationModal
        title={t('orders:deleteProrationTitle', { defaultValue: 'Delete Proration' })}
        confirmationMessage={t('orders:deleteProrationMessage', { defaultValue: 'Are you sure you want to delete this proration?' })}
        isOpen={isDeleteDialogOpen}
        onConfirm={onConfirmDelete}
        onClose={onCancelDelete}
        isRemoveLoading={isDeleteLoading}
        buttonConfirmText={t('common:delete', { defaultValue: 'Delete' })}
      />
    </Box>
  );
};
